import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSettings } from 'react-icons/fi';

const CookieConsentBanner = ({ onAccept, onReject, onCustomize, preferences, setPreferences }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consentStatus = localStorage.getItem('cookieConsent');
    if (!consentStatus) {
      // Show banner after a slight delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    }));
    setShowBanner(false);
    onAccept();
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    }));
    setShowBanner(false);
    onReject();
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'customized');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowBanner(false);
    onCustomize(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Don't toggle necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
      >
        <div className="max-w-4xl mx-auto glass-card p-6 rounded-xl shadow-xl border border-neutral-light/20">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-semibold">Cookie Consent</h3>
            <button 
              onClick={() => setShowBanner(false)} 
              className="text-neutral-dark/60 hover:text-primary-600 transition-colors"
              aria-label="Close cookie banner"
            >
              <FiX size={20} />
            </button>
          </div>
          
          <div className="prose prose-sm max-w-none mb-4">
            <p>
              We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. 
              By clicking "Accept All", you consent to our use of cookies. Read our{' '}
              <a href="/cookies" className="text-primary-600 hover:underline">Cookie Policy</a> and{' '}
              <a href="/privacy" className="text-primary-600 hover:underline">Privacy Policy</a> for more information.
            </p>
          </div>
          
          {showDetails && (
            <div className="mb-6 border-t border-b border-neutral-light/20 py-4 my-4">
              <h4 className="font-semibold mb-3">Cookie Preferences</h4>
              
              <div className="space-y-3">
                {/* Necessary Cookies - Always enabled */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Necessary</p>
                    <p className="text-xs text-neutral-dark/70">Essential for the website to function properly</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={true}
                      readOnly
                      className="sr-only" 
                      id="necessary-cookies"
                    />
                    <label 
                      htmlFor="necessary-cookies"
                      className="relative inline-block w-10 h-5 cursor-not-allowed"
                    >
                      <span className="absolute inset-0 rounded-full bg-primary-500"></span>
                      <span className="absolute w-3 h-3 bg-white rounded-full transform translate-y-1 translate-x-6 transition-transform"></span>
                    </label>
                  </div>
                </div>
                
                {/* Analytics Cookies */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Analytics</p>
                    <p className="text-xs text-neutral-dark/70">Help us improve by tracking anonymous usage data</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics}
                      onChange={() => togglePreference('analytics')}
                      className="sr-only" 
                      id="analytics-cookies"
                    />
                    <div 
                      className="relative inline-block w-10 h-5 cursor-pointer"
                      onClick={() => togglePreference('analytics')}
                    >
                      <span className={`absolute inset-0 rounded-full transition-colors duration-200 ${preferences.analytics ? 'bg-primary-500' : 'bg-neutral-dark/30'}`}></span>
                      <span className={`absolute w-3 h-3 bg-white rounded-full transform translate-y-1 transition-transform duration-200 ${preferences.analytics ? 'translate-x-6' : 'translate-x-1'}`}></span>
                    </div>
                  </div>
                </div>
                
                {/* Marketing Cookies */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Marketing</p>
                    <p className="text-xs text-neutral-dark/70">Allow us to provide personalized ads on other platforms</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={preferences.marketing}
                      onChange={() => togglePreference('marketing')}
                      className="sr-only" 
                      id="marketing-cookies"
                    />
                    <div 
                      className="relative inline-block w-10 h-5 cursor-pointer"
                      onClick={() => togglePreference('marketing')}
                    >
                      <span className={`absolute inset-0 rounded-full transition-colors duration-200 ${preferences.marketing ? 'bg-primary-500' : 'bg-neutral-dark/30'}`}></span>
                      <span className={`absolute w-3 h-3 bg-white rounded-full transform translate-y-1 transition-transform duration-200 ${preferences.marketing ? 'translate-x-6' : 'translate-x-1'}`}></span>
                    </div>
                  </div>
                </div>
                
                {/* Preferences Cookies */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Preferences</p>
                    <p className="text-xs text-neutral-dark/70">Remember your settings and provide enhanced functionality</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={preferences.preferences}
                      onChange={() => togglePreference('preferences')}
                      className="sr-only" 
                      id="preference-cookies"
                    />
                    <div 
                      className="relative inline-block w-10 h-5 cursor-pointer"
                      onClick={() => togglePreference('preferences')}
                    >
                      <span className={`absolute inset-0 rounded-full transition-colors duration-200 ${preferences.preferences ? 'bg-primary-500' : 'bg-neutral-dark/30'}`}></span>
                      <span className={`absolute w-3 h-3 bg-white rounded-full transform translate-y-1 transition-transform duration-200 ${preferences.preferences ? 'translate-x-6' : 'translate-x-1'}`}></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap gap-3 justify-end">
            <button 
              onClick={() => setShowDetails(!showDetails)} 
              className="flex items-center text-sm text-neutral-dark/70 hover:text-primary-600 transition-colors"
            >
              <FiSettings className="mr-1" /> {showDetails ? 'Hide Preferences' : 'Customize'}
            </button>
            
            <div className="flex gap-3">
              <button onClick={handleRejectAll} className="btn-outline text-sm py-2 px-4">
                Reject All
              </button>
              
              {showDetails ? (
                <button onClick={handleSavePreferences} className="btn-primary text-sm py-2 px-4">
                  Save Preferences
                </button>
              ) : (
                <button onClick={handleAcceptAll} className="btn-primary text-sm py-2 px-4">
                  Accept All
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentBanner;